<template>
  <v-card>
    <v-toolbar dense>
      <v-text-field
        v-model="searchString"
        hide-details
        prepend-inner-icon="search"
        single-line
        clearable
        @click:clear="clearSearchInput"
      />

      <template v-slot:extension>
        <v-tabs
          v-model="selectedLanguage"
          centered
          slider-color="black"
          @change="searchInputChanged"
        >
          <v-tab>Alle</v-tab>
          <v-tab>DE</v-tab>
          <v-tab>FR</v-tab>
          <v-tab>IT</v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-layout class="keywordsList">
      <u-loader
        v-if="isLoading"
        class="w-100 mt-4"
      />
      <v-list
        v-else
        ref="listView"
        two-line
        style="width: 100%"
      >
        <template v-for="(item, index) in allListItems">
          <v-list-tile
            v-if="index + 1 <= allListItems.length"
            :key="index"
            :title="item.keyword"
            :to="'/keywords/' + item.keywordId"
          >
            <v-list-tile-content>
              <v-list-tile-title>{{ item.titel }}</v-list-tile-title>
              <v-list-tile-sub-title>
                <keyword-title :keyword="item" />
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider
            :key="'div-' + index"
            class="m-0"
          />
        </template>
        <template>
          <v-list-tile
            v-if="totalNumberOfItems > allListItems.length"
            :key="index"
            @click="growKeywordsList()"
          >
            <v-list-tile-content>
              <v-list-tile-title class="text-center text-primary">
                Mehr Stichwörter laden
              </v-list-tile-title>
              <v-list-tile-sub-title class="text-center">
                <b>Achtung!</b>
                <p>Dies kann die Performance beeinträchtigen</p>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>
      </v-list>
    </v-layout>
  </v-card>
</template>

<script>
import { MultiLangMixin } from "@/mixins/MultilanguageContentsMixin";
import { GetAllKeywords } from "@/services/keywords.service.js";
import KeywordTitle from "@/components/keywords/KeywordTitle.vue";

export default {
  components: {
    KeywordTitle,
  },
  mixins: [MultiLangMixin],
  props: {
    propertyToDisplay: {
      type: String,
      required: true,
    },
    propertyToSearch: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedListItem: null,
      isLoading: false,
      errorObject: null,
      searchString: "",
      allListItems: [],
      totalNumberOfItems: 0,
      maxNumberOfItems: 25,
      selectedLanguage: 0,
      currentArrayPosition: 0,
      currentScrollPosition: 0,
      searchQueryQueue: [],
      languages: [null, "de-CH", "fr-CH", "it-CH"],
    };
  },
  watch: {
    searchString() {
      this.searchQueryQueue.push(new Date().getTime());
      setTimeout(() => {
        if (this.searchQueryQueue.length > 1) {
          this.searchQueryQueue = this.searchQueryQueue.slice(1);
          return;
        }

        this.currentScrollPosition = 0;
        this.searchQueryQueue = [];
        this.reloadAllKeyWords();
      }, 400);
    },
    selectedLanguage() {
      this.currentScrollPosition = 0;
      this.reloadAllKeyWords();
    },
  },
  mounted() {
    this.reloadAllKeyWords();
  },
  methods: {
    reloadAllKeyWords() {
      this.errorObject = null;
      this.isLoading = true;

      GetAllKeywords(
        this.maxNumberOfItems,
        this.searchString,
        this.languages[this.selectedLanguage],
        (result) => {
          this.totalNumberOfItems = result.numberOfItems;
          this.allListItems = result.keywords;

          this.isLoading = false;

          setTimeout(() => {
            var list = this.$el.querySelector(".keywordsList");
            list.scrollTop = this.currentScrollPosition;
          }, 0);
        },
        (error) => {
          this.errorObject = error;
          this.isLoading = false;
        },
      );
    },

    growKeywordsList() {
      var list = this.$el.querySelector(".keywordsList");
      this.currentScrollPosition = list.scrollTop;

      this.maxNumberOfItems = this.maxNumberOfItems + 25;
      this.reloadAllKeyWords();
    },

    multilangSubtitle(item) {
      var textDe = this.getContentForLanguage(item.keywordContents, "de-CH");
      var textFr = this.getContentForLanguage(item.keywordContents, "fr-CH");
      var textIt = this.getContentForLanguage(item.keywordContents, "it-CH");

      return textDe + "/" + textFr + "/" + textIt;
    },

    clearSearchInput() {
      this.searchString = "";
    },

    searchInputChanged() {
      if (
        (!this.searchString || this.searchString === "") &&
        this.selectedLanguage == 0
      ) {
        this.resetHelperLists();
      } else {
        let searchString = this.searchString.toLocaleLowerCase();
        let hasSearchString =
          !this.searchString || this.searchString === "" ? false : true;

        this.filteredListItems = this.allListItems.filter((item) => {
          var isMatch = false;

          if (hasSearchString) {
            isMatch =
              item[this.propertyToSearch[0]]
                .toLocaleLowerCase()
                .indexOf(searchString) != -1;
          } else {
            isMatch = true;
          }

          if (isMatch) {
            if (item.originalLanguage == null) {
              isMatch = true; // if original language is not set it is a new keyword and this cannot be filtered by language
            } else if (this.selectedLanguage == 1) {
              isMatch = item.originalLanguage == "de-CH";
            } else if (this.selectedLanguage == 2) {
              isMatch = item.originalLanguage == "fr-CH";
            } else if (this.selectedLanguage == 3) {
              isMatch = item.originalLanguage == "it-CH";
            }
          }

          return isMatch;
        });

        this.selectedListItem = this.filteredListItems[0];
        this.resetGrowableKeywordsList();
      }
    },
  },
};
</script>

<style>
.searchSelectListWrapper .v-tabs__wrapper {
  background-color: #f5f5f5;
}

.v-tabs__container--centered {
  background-color: #f5f5f5;
}
</style>
<style scoped>
.listBoxContainer {
  height: 100%;
}
.selectedKeyWordContainer {
  width: 100%;
  padding: 8px;
  font-size: initial;
  color: gray;
  border-bottom: 1px solid black;
}

.keywordsList {
  height: calc(100% - 96px);
  width: 100%;
  overflow-y: auto;
}
</style>
